import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import {
  AppWrapper,
  Container,
  ContentContainer,
} from "./styles";
import TopButtons from "./Components/TopButtons";
import Intro from "./Components/Intro";
import Question from "./Components/Question";
import Results from "./Components/Results";
import PatientSelector from "./Components/PatientSelector";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import { ThemeProvider } from "styled-components";
import { usePatients } from "./contexts/PatientsContext";
import Progressbar from "./Components/Progressbar";
import useLocalStorage from "./hooks/useLocalStorage";
import { theme } from "./theme";
import Tutorial from "./Components/Tutorial";
import Restart from "./Components/Restart";
import PatientDetailsPage from "./Components/PatientDetails";
import Intersection from "./Components/Intersection";
import Footnotes from "./Components/Footnotes";
import Background from "./Components/Background";
import { references } from "./contexts/questions";
import { abbreviations } from "./contexts/questions";
import QuizBackground from "./Components/QuizBackground";
import { scrollToTop } from "./utils";
import Modal from "./Components/Modal";
import InternalMessage from "./Components/InternalMessage";


function App() {
  const patients = usePatients();
  const [name, setName] = useState(null);
  const [show, setShow] = useState(true);
  const [answers, setAnswers] = useLocalStorage(`answer-${name}`, false);
  const [active, setActive] = useState(0);
  const history = useHistory();
  const location = useLocation();
  const [referencesContent, setReferencesContent] = useState(null);
  const [abbreviationsContent, setAbbreviationsContent] = useState(null);
  const [isIdle, setIsIdle] = useState(true);
  const [showICS, setShowICS] = useState(false);
  const [videoSrc, setVideoSrc] = useState(
    `./videos/${patients[0].name}_door-1_idle-1.mp4`
  );
  const questions = useMemo(
    () =>
      name ? patients.find((patient) => patient.name === name).questions : [],
    [name, patients]
  );

  // Variants
  const [showLogin, setShowLogin] = useState(false);
  const [showInternalUse, setShowInternalUse] = useState(false);
  const [showStandalone, setShowStandalone] = useState(false);
  const [showBrand, setShowBrand] = useState(false);

  useEffect(() => {
    // when url changes, scroll to top of the page if on mobile
    scrollToTop();
  }, [location]);

  const resetProgress = useCallback(() => {
    history.push("/");
    setAnswers(null);
    setIsIdle(true);
    const bgImage = document.getElementById("background-image");
                   bgImage.src = `./background-imgs/Mary_door-1.png`; 
                   bgImage.style = 'opacity:1';
  }, [setAnswers, history]);

  // update the footnotes when route changes (for non-question pages)
  useEffect(() => {
   
    if (location.pathname === "/") {
      setReferencesContent(references["/"]);
      setAbbreviationsContent(abbreviations["/"]);
      resetProgress();
    } 
    else if (location.pathname === "/international-statement") {
      setReferencesContent(references["statement"]);
      setAbbreviationsContent(abbreviations["statement"]);
    }
    else if (location.pathname.includes("tutorial")) {
      setReferencesContent("");
      setAbbreviationsContent("");
      setAnswers(null);
    } 
    else {
      const path = location.pathname.split("/");

      if (references[path[path.length - 1]]) {
        setReferencesContent(references[path[path.length - 1]]);
      }

      if (abbreviations[path[path.length - 1]]) {
        setAbbreviationsContent(abbreviations[path[path.length - 1]]);
      }
    }
  }, [location]);

  const isDetails = location.pathname.includes("/details");
  const isExit = location.pathname.includes("/exit");
  const isIntersection = location.pathname.includes("/intersection");
  const isStatement = location.pathname.includes("/international-statement");

  // if it should include the videos for the quiz (the tower wobbling and falling)
  const isQuiz =
    (location.pathname.includes(`/${name}/`) &&
      !location.pathname.includes("tutorial")) ||
    location.pathname === "/exit";

  return (
    <ThemeProvider theme={theme}>

      {showInternalUse && (<InternalMessage />)}

      <AppWrapper 
        style={{"paddingBottom" : showInternalUse ? "40px" : "0"}}
        className={showStandalone ? "standalone" : ""}>

        {showBrand && (<Header />)}

        {name && !location.pathname.includes("tutorial") ? (
          <Container style={{ position: "relative", top: "24px" }}>

            
            <Progressbar
              name={name}
              questions={questions}
              answers={answers}
              active={active}
              disabled={isDetails || isExit || isIntersection || isStatement}
            />

            <TopButtons
              goToIntersection={active >= 8}
              active={active}
              name={name}
              setShowICS={setShowICS}
              showICS={showICS}
              resetProgress={resetProgress}
            />
          </Container>
        ) : null }

        <ContentContainer id="content" isResults={location.pathname.includes("/results")}>

          <Background />

          {isQuiz && (
            <QuizBackground
              answers={answers}
              questions={questions}
              name={name}
              isIdle={isIdle}
              showICS={showICS}
            />
          )}

          <Switch>
            {/* Home page */}
            <Route
              exact
              path="/"
              render={() => (
                <>
                  <Intro setName={setName} />
                </>
              )}
            />

            {/* Patient Selector */}
            <Route
              exact
              path="/patient-selector"
              render={() => (
                <PatientSelector
                  setVideoSrc={setVideoSrc}
                  setName={setName}
                  name={name}
                />
              )}
            />
            {/* Render routes for each patient */}
            {patients.map(
              ({ name: patientName, questions, details, fullname }) => {
                // Routes for patient questions

                return [
                  ...questions.map((question, index) => (
                    <Route
                      exact
                      path={`/${patientName}/${index}`}
                      render={() => (
                        <Question
                          key={`${patientName}-${index}`}
                          answers={answers}
                          setAnswers={setAnswers}
                          setActive={setActive}
                          setName={setName}
                          index={index}
                          name={patientName}
                          questions={questions}
                          fullname={fullname}
                          setReferences={setReferencesContent}
                          setAbbreviations={setAbbreviationsContent} 
                          setIsIdle={setIsIdle}
                          setShowICS={setShowICS}
                          showICS={showICS}
                        />
                      )}
                    />
                  )),
                  // Route for patient result screen
                  <Route
                    exact
                    path={`/${patientName}/results`}
                    render={() => (
                      <Results
                        setName={setName}
                        name={patientName}
                        setActive={setActive}
                        questions={questions}
                        setReferences={setReferencesContent}
                        setAbbreviations={setAbbreviationsContent} 
                        answers={answers}
                        resetProgress={resetProgress}
                        setIsIdle={setIsIdle}
                      />
                    )}
                  />,
                  <Route
                    exact
                    path={`/${patientName}/details`}
                    render={() => (
                      <PatientDetailsPage
                        setName={setName}
                        name={name}
                        patientName={patientName}
                        questions={questions}
                        details={details}
                      />
                    )}
                  />,
                  <Route
                    exact
                    path={`/${patientName}/tutorial`}
                    render={() => (
                      <Tutorial
                        name={name}
                        setName={setName}
                        patientName={patientName}
                        setVideoSrc={setVideoSrc}
                        setShow={setShow}
                      ></Tutorial>
                    )}
                  />,
                  <Route
                    exact
                    path={`/${patientName}/intersection`}
                    render={() => (
                      <Intersection
                        name={patientName}
                        patientName={patientName}
                        setName={setName}
                        details={details}
                        active={active}
                      ></Intersection>
                    )}
                  />,
                ];
              }
            )}
            <Route
              exact
              path="/exit"
              render={() => (
                <Restart setShowICS={setShowICS} name={name} resetProgress={resetProgress} />
              )}
            ></Route>
            {/* If route not found redirect to the homepage */}
            <Redirect to="/" />
          </Switch>
        </ContentContainer>
        <Modal 
          show={show}
          setShow={setShow}
          showLogin={showLogin}
         />
        <Footnotes 
          references={referencesContent} 
          abbreviations={abbreviationsContent}  
          showICS={showICS}
          showStandalone={showStandalone}
        />
        {showBrand && (<Footer />)}
      </AppWrapper>
    </ThemeProvider>
  );
}

export default App;
